import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  HStack,
  Text,
} from "@chakra-ui/react";
import { FC } from "react";

import { SectionCard } from "~/src/components/common/SectionCard";
import { chakraFactory } from "~/src/lib/chakra-ui";

import { RobeeTagCard } from "./RobeeTagCard";

const StyledSectionCard = chakraFactory(SectionCard, {
  baseStyle: {
    maxWidth: "800px",
    margin: "24px auto 0 auto", // Consistent with standard section spacing pattern
  },
});

const StyledCodeWrapper = chakraFactory(Box, {
  baseStyle: {
    background: "#FAFAFA",
    border: "1px solid #DADADA",
    borderRadius: "2px",
    padding: "12px",
    fontSize: "10px",
    letterSpacing: "0.02em",
    color: "#000000",
    whiteSpace: "pre-wrap",
    fontFamily: "monospace",
    maxHeight: "300px",
    overflowY: "auto",
  },
});

const StyledDescriptionText = chakraFactory(Text, {
  baseStyle: {
    fontSize: "13px",
    color: "#666666",
    marginBottom: "12px",
  },
});

const StyledAccordion = chakraFactory(Accordion, {
  baseStyle: {
    width: "100%",
  },
});

const StyledAccordionItem = chakraFactory(AccordionItem, {
  baseStyle: {
    border: "1px solid #DADADA",
    borderRadius: "2px",
    marginBottom: "8px",
    background: "#FFFFFF",
    overflow: "hidden",
    _last: {
      marginBottom: 0,
    },
  },
});

const StyledAccordionButton = chakraFactory(AccordionButton, {
  baseStyle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    fontSize: "13px",
    fontWeight: "500",
    color: "#000000",
    _hover: {
      background: "#FAFAFA",
    },
    _focus: {
      boxShadow: "none",
    },
  },
});

const StyledAccordionPanel = chakraFactory(AccordionPanel, {
  baseStyle: {
    padding: "0 16px 16px 16px",
    borderTop: "1px solid #DADADA",
  },
});

export type OverviewLinkedModule = {
  id: string;
  name: string;
  tagContent: string | null;
};

type Props = {
  embedTag: string;
  mergedTagPreview: string | null;
  enabledModuleCount: number;
  totalModuleCount: number;
  linkedModules: OverviewLinkedModule[];
};

export const OverviewTab: FC<Props> = ({
  embedTag,
  mergedTagPreview,
  enabledModuleCount,
  totalModuleCount,
  linkedModules,
}) => {
  return (
    <Box>
      {/* Robee Tag Card - existing component with its own margin */}
      <RobeeTagCard snippet={embedTag} />

      {/* Merged Output Preview - centered with consistent margin */}
      <StyledSectionCard description="JavaScriptタグ">
        <StyledDescriptionText>
          有効モジュール: {enabledModuleCount}件 / 全{totalModuleCount}件
        </StyledDescriptionText>

        <StyledCodeWrapper>
          {mergedTagPreview || "// 有効なモジュールがありません"}
        </StyledCodeWrapper>
      </StyledSectionCard>

      {linkedModules.length > 0 && (
        <StyledSectionCard description="配信条件連動モジュール">
          <StyledDescriptionText>
            有効な配信条件連動モジュール（{linkedModules.length}
            件）。CDNマージから除外され、訪問時にリンク先の配信条件を満たした場合のみ実行されます。
          </StyledDescriptionText>

          <StyledAccordion allowMultiple>
            {linkedModules.map((module) => (
              <StyledAccordionItem key={module.id}>
                <StyledAccordionButton>
                  <HStack spacing="8px" flex="1" textAlign="left">
                    <Text fontSize="13px" fontWeight="500">
                      {module.name}
                    </Text>
                    <Badge colorScheme="purple" size="sm">
                      配信条件連動
                    </Badge>
                  </HStack>
                  <AccordionIcon />
                </StyledAccordionButton>
                <StyledAccordionPanel>
                  <StyledCodeWrapper marginTop="12px">
                    {module.tagContent || "// (空)"}
                  </StyledCodeWrapper>
                </StyledAccordionPanel>
              </StyledAccordionItem>
            ))}
          </StyledAccordion>
        </StyledSectionCard>
      )}
    </Box>
  );
};
